import * as t from 'io-ts';
import moment from 'moment';
import { createActions } from 'redux-actions';

import { ENDPOINTS_COMPETITIONS, ENDPOINTS_CONSULTANT, ENDPOINTS_USER } from '../config/';
import { localeData } from '../locales/data';
import {
  CompetitionOptions,
  CompetitionsLevels,
  CompetitionTable,
  CompetitionUserInfo,
  Results,
} from '../reducers/CompetitionsReducer/CompetitionsReducer.types';
import {
  defaultErrorMessage,
  globalFetch,
  ioTsValidationError,
  isAction,
  sendFlashMessageEmail,
  webSimApiErrorFlashMessages,
} from '../shared/helpers/fetch-helper';
import { logErrorService } from '../shared/services';

import { sessionExpire } from './AuthenticationActions';
import {
  dashboardCompetitionsRequest,
  dashboardCompetitionsResponse,
  dashboardCompetitionsResponseFailure,
} from './Dashboard/CompetitionActions';
import { addFlashMessages } from './FlashMessagesActions';
import { invalidRequest } from './GenericActions';
import { fetchPowerPoolRegions, fetchSpcBoardChoices } from '../shared/api/api';

export const {
  responseCompetitionSuccess,
  responseCompetitionAgreementSuccess,
  userCompetitionsSuccess,
  resetSelectedColumnsCompetitions,
  requestUserCompetitions,
  userCompetitionsFailure,
  requestCompetitionAcceptAgreement,
  responseCompetitionAcceptAgreementFailure,
  responseCompetitionAcceptAgreementSuccess,
  responseCompetitionAgreementFailure,
  requestCompetitionAgreement,
  responseConsultantLeaderboardCompetitions,
  changeCurrentCompetition,
  requestCompetitionFilters,
  responseCompetitionFiltersSuccess,
  responseCompetitionFiltersFailure,
  responseCompetitionsFiltersCancel,
  responseConsultantLeaderboardModalCompetitions,
  clearConsultantLeaderboardModalCompetitions,
  clearConsultantLeaderboardCompetitions,
  toggleGuidelinesModal,
  requestAllCompetitions,
  responseAllCompetitionsFailure,
  responseAllCompetitionsSuccess,
  requestConsultantLeaderboardCompetitions,
  requestCompetition,
  responseCompetitionFailure,
  requestCompetitionsLevels,
  responseCompetitionsLevelsSuccess,
  responseCompetitionsLevelsFailure,
  changeAggregation,
  changeCompetitionTab,
  setLogoutCompetition,
  hideAgreementsCompetitions,
  cleanCompetition,
} = createActions(
  {
    RESPONSE_COMPETITION_SUCCESS: (json: any, competitionId: string) => ({ json, competitionId }),
    RESPONSE_COMPETITION_AGREEMENT_SUCCESS: (json: any) => ({ json }),
    USER_COMPETITIONS_SUCCESS: (json: any) => json,
    RESPONSE_CONSULTANT_LEADERBOARD_COMPETITIONS: (consultantType: string, data: any) => ({ consultantType, data }),
    TOGGLE_GUIDELINES_MODAL: (value: boolean) => value,
    RESPONSE_COMPETITIONS_LEVELS_SUCCESS: (json: any) => json,
    REQUEST_COMPETITION_FILTERS: (competitionId: string, competitionType: string) => ({ competitionId, competitionType }),
  },
  'RESET_SELECTED_COLUMNS_COMPETITIONS',
  'AGREED_WITH_GUIDELINES_COMPETITIONS',
  'REQUEST_USER_COMPETITIONS',
  'USER_COMPETITIONS_FAILURE',
  'REQUEST_COMPETITION_ACCEPT_AGREEMENT',
  'RESPONSE_COMPETITION_ACCEPT_AGREEMENT_FAILURE',
  'RESPONSE_COMPETITION_ACCEPT_AGREEMENT_SUCCESS',
  'RESPONSE_COMPETITION_AGREEMENT_FAILURE',
  'REQUEST_COMPETITION_AGREEMENT',
  'CHANGE_CURRENT_COMPETITION',
  'RESPONSE_COMPETITION_FILTERS_SUCCESS',
  'RESPONSE_COMPETITION_FILTERS_CANCEL',
  'RESPONSE_COMPETITION_FILTERS_FAILURE',
  'RESPONSE_CONSULTANT_LEADERBOARD_MODAL_COMPETITIONS',
  'CLEAR_CONSULTANT_LEADERBOARD_MODAL_COMPETITIONS',
  'CLEAR_CONSULTANT_LEADERBOARD_COMPETITIONS',
  'REQUEST_ALL_COMPETITIONS',
  'RESPONSE_ALL_COMPETITIONS_FAILURE',
  'RESPONSE_ALL_COMPETITIONS_SUCCESS',
  'REQUEST_CONSULTANT_LEADERBOARD_COMPETITIONS',
  'REQUEST_COMPETITION',
  'RESPONSE_COMPETITION_FAILURE',
  'REQUEST_COMPETITIONS_LEVELS',
  'RESPONSE_COMPETITIONS_LEVELS_FAILURE',
  'CHANGE_AGGREGATION',
  'CHANGE_COMPETITION_TAB',
  'SET_LOGOUT_COMPETITION',
  'HIDE_AGREEMENTS_COMPETITIONS',
  'CLEAN_COMPETITION'
);

export const { competitionsTableSuccess, requestCompetitionsTable, competitionsTableFailure } = createActions(
  {
    COMPETITIONS_TABLE_SUCCESS: (json: any) => json,
  },
  'REQUEST_COMPETITIONS_TABLE',
  'COMPETITIONS_TABLE_FAILURE'
);

export const { prizesTableSuccess, requestPrizesTable, prizesTableFailure } = createActions(
  {
    PRIZES_TABLE_SUCCESS: (json: any) => json,
  },
  'REQUEST_PRIZES_TABLE',
  'PRIZES_TABLE_FAILURE'
);

export const { referralsTableSuccess, requestReferralsTable, referralsTableFailure, referralsTableModalSuccess } = createActions(
  {
    REFERRALS_TABLE_SUCCESS: (json: any) => json,
  },
  'REQUEST_REFERRALS_TABLE',
  'REFERRALS_TABLE_FAILURE',
  'REFERRALS_TABLE_MODAL_SUCCESS'
);

export const { powerPoolTableSuccess, requestPowerPoolTable, powerPoolTableFailure, powerPoolTableModalSuccess } = createActions(
  {
    POWER_POOL_TABLE_SUCCESS: (json: any) => json,
  },
  'REQUEST_POWER_POOL_TABLE',
  'POWER_POOL_TABLE_FAILURE',
  'POWER_POOL_TABLE_MODAL_SUCCESS'
);

export const { spcLeaderboardTableSuccess, requestSpcLeaderboardTable, spcLeaderboardTableFailure, spcLeaderboardTableModalSuccess } =
  createActions(
    { SPC_LEADERBOARD_TABLE_SUCCESS: (json: any) => json },
    'REQUEST_SPC_LEADERBOARD_TABLE',
    'SPC_LEADERBOARD_TABLE_FAILURE',
    'SPC_LEADERBOARD_TABLE_MODAL_SUCCESS'
  );

export const addGuidelinesModalPromise =
  (value: boolean) =>
  async (dispatch: any): Promise<void> =>
    new Promise(async (resolve, _) => {
      dispatch(toggleGuidelinesModal(value));
      resolve();
    });

export const acceptCompetition =
  (competitionId: string) =>
  async (dispatch: any, getState: any): Promise<object> => {
    if (competitionId === undefined) {
      return dispatch(invalidRequest());
    }

    return fetch(`${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/accept`, {
      method: 'POST',
      credentials: 'include',
    }).catch((error: any) => {
      const state = getState();

      return sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));
    });
  };

export const getUserCompetitions =
  (userId: string | undefined, fetchToTrue: boolean = true) =>
  async (dispatch: any, getState: any): Promise<object> => {
    if (userId === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestUserCompetitions(fetchToTrue));
    dispatch(dashboardCompetitionsRequest());

    return globalFetch(
      `${ENDPOINTS_USER.user}/${userId}/competitions`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => {
        dispatch(dashboardCompetitionsResponseFailure());

        return dispatch(userCompetitionsFailure());
      }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'USER_COMPETITIONS_FAILURE')) {
          return dispatch(userCompetitionsFailure());
        }

        if (t.exact(CompetitionUserInfo).decode(json).isRight()) {
          if (json.count >= 0) {
            dispatch(dashboardCompetitionsResponse(json));

            return dispatch(userCompetitionsSuccess(json));
          } else {
            // It will be reported to sentry the io-ts validation errors
            ioTsValidationError(json, CompetitionUserInfo);

            return dispatch(userCompetitionsFailure());
          }
        } else {
          logErrorService(json, json);
          defaultErrorMessage(dispatch);

          return dispatch(userCompetitionsFailure());
        }
      })
      .catch((error: any) => {
        const state = getState();

        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(userCompetitionsFailure());
      });
  };

export const acceptAggrement =
  (competitionId: string, userId: string) =>
  async (dispatch: any, getState: any): Promise<object> => {
    if (competitionId === undefined) {
      return dispatch(invalidRequest());
    }
    const state = getState();
    dispatch(requestCompetitionAcceptAgreement());

    return fetch(`${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/accept`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          dispatch(getUserCompetitions(userId));

          return dispatch(responseCompetitionAcceptAgreementSuccess());
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          dispatch(sessionExpire());

          return responseCompetitionAcceptAgreementFailure();
        } else if (response.status === 500) {
          logErrorService(response.statusText, response.status);

          return dispatch(responseCompetitionAcceptAgreementFailure());
        } else {
          logErrorService(response.statusText, response.status);

          return response.json();
        }
      })
      .then(async (json: any): Promise<object> => {
        if (
          json &&
          (isAction(json, 'RESPONSE_COMPETITION_ACCEPT_AGREEMENT_SUCCESS') || json.type === 'RESPONSE_COMPETITION_ACCEPT_AGREEMENT_FAILURE')
        ) {
          return json;
        } else if (json.detail) {
          logErrorService(json.detail, json.detail);
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }]));

          return dispatch(responseCompetitionAcceptAgreementFailure());
        } else {
          logErrorService(json, json);
          defaultErrorMessage(dispatch);

          return dispatch(responseCompetitionAcceptAgreementFailure());
        }
      })
      .catch((error: any) => {
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(responseCompetitionAcceptAgreementFailure());
      });
  };

function filterQuery(filters: any) {
  if (Object.keys(filters).length === 0 && filters.constructor === Object) {
    return '';
  } else {
    const newFilters = Object.keys(filters)
      .map((key: any) => {
        // We have new APIs (e.g. Genius Leaderboard) that doesn't support encoded filters
        // This will allow us to keep original query if the key starts with dollar sign ($)
        if (key.startsWith('$')) return filters[key];
        return encodeURIComponent(filters[key]);
      })
      .join('&');

    return `${newFilters}`;
  }
}

export const getAgreement =
  (competitionId: string) =>
  async (dispatch: any, getState: any): Promise<object> => {
    if (competitionId === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestCompetitionAgreement());

    return globalFetch(
      `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/agreement`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseCompetitionAgreementFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_COMPETITION_AGREEMENT_FAILURE')) {
          return json;
        } else if (json.details) {
          logErrorService(json.details, json.details);
          dispatch(addFlashMessages([{ msg: json.details, defaultMessage: localeData.en[json.details] }]));

          return dispatch(responseCompetitionAgreementFailure());
        } else if (json.detail) {
          logErrorService(json.detail, json.detail);
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));

          return dispatch(responseCompetitionAgreementFailure());
        } else {
          return dispatch(responseCompetitionAgreementSuccess(json));
        }
      })
      .catch((error: any) => {
        const state = getState();

        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(responseCompetitionAgreementFailure());
      });
  };

export const getCompetitionsTable =
  (
    competitionId: string,
    tableId: string,
    limit: number,
    offset: number,
    filters: any,
    sort?: any,
    order?: boolean,
    typeAggregate?: string,
    isModal: boolean = false
  ) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    if (!competitionId || competitionId === '' || tableId === undefined || (limit === undefined && offset === undefined)) {
      return dispatch(invalidRequest());
    }
    const queryFilters = filters !== undefined ? `&${filterQuery(filters)}` : '';
    const newSort = sort;
    const queryOrder =
      newSort !== undefined ? (order ? `&order=${encodeURIComponent(newSort)}` : `&order=-${encodeURIComponent(newSort)}`) : '';
    const queryAggregate = typeAggregate && typeAggregate !== 'individual' ? `&aggregate=${encodeURIComponent(typeAggregate)}` : '';
    const link = `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/boards/${tableId}`;
    const queryString = `${queryFilters}${queryOrder}${queryAggregate}`;
    dispatch(requestCompetitionsTable());

    return globalFetch(
      `${link}?limit=${limit}&offset=${offset}${queryString}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(competitionsTableFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'COMPETITIONS_TABLE_FAILURE')) {
          return json;
        } else if (t.exact(CompetitionTable).decode(json).isRight()) {
          if (json.count >= 0) {
            if (isModal) {
              return dispatch(responseConsultantLeaderboardModalCompetitions(json));
            } else {
              return dispatch(competitionsTableSuccess(json));
            }
          } else {
            logErrorService(json, json);
          }
        } else {
          logErrorService(json, json);
        }

        return dispatch(competitionsTableFailure());
      })
      .catch((error: any) => {
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(competitionsTableFailure());
      });
  };

export const getPrizesTable =
  (competitionId: string, limit: number, offset: number, filters: any, sort?: any, order?: boolean) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    dispatch(requestPrizesTable());

    const newSort = sort;
    const queryOrder =
      newSort !== undefined ? (order ? `&order=${encodeURIComponent(newSort)}` : `&order=-${encodeURIComponent(newSort)}`) : '';
    const queryString = `${queryOrder}`;

    const link = `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/boards/prize`;

    return globalFetch(
      `${link}?limit=${limit}&offset=${offset}${queryString}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(prizesTableFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'COMPETITIONS_TABLE_FAILURE')) {
          return json;
        } else if (json.count >= 0) {
          dispatch(prizesTableSuccess(json));
        }

        return dispatch(prizesTableFailure());
      })
      .catch((error: any) => {
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(prizesTableFailure());
      });
  };

interface ReferralsTableRequestParameters {
  competitionId: string;
  limit: number;
  offset: number;
  filters: any;
  sort?: any;
  order?: boolean;
  typeAggregate?: string;
  isModal?: boolean;
}

export const getReferralsTable =
  ({ competitionId, limit, offset, filters, sort, order, typeAggregate, isModal = false }: ReferralsTableRequestParameters) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    dispatch(requestReferralsTable());

    const queryFilters = filters !== undefined ? `&${filterQuery(filters)}` : '';
    const newSort = sort;
    const queryOrder =
      newSort !== undefined ? (order ? `&order=${encodeURIComponent(newSort)}` : `&order=-${encodeURIComponent(newSort)}`) : '';
    const queryAggregate = typeAggregate && typeAggregate !== 'individual' ? `&aggregate=${encodeURIComponent(typeAggregate)}` : '';

    const queryString = `${queryFilters}${queryOrder}${queryAggregate}`;

    const link = `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/boards/referral`;

    return globalFetch(
      `${link}?limit=${limit}&offset=${offset}${queryString}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(referralsTableFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'REFERRALS_TABLE_FAILURE')) {
          return json;
        } else if (json.count >= 0) {
          if (isModal) {
            dispatch(referralsTableModalSuccess());
            return dispatch(responseConsultantLeaderboardModalCompetitions(json));
          }
          return dispatch(referralsTableSuccess(json));
        }

        return dispatch(referralsTableFailure());
      })
      .catch((error: any) => {
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(referralsTableFailure());
      });
  };

interface PowerPoolTableRequestParameters extends ReferralsTableRequestParameters {
  boardName?: string;
}

export const getPowerPoolTable =
  ({
    competitionId,
    limit,
    offset,
    filters = {},
    sort,
    order,
    typeAggregate,
    isModal = false,
    boardName,
  }: PowerPoolTableRequestParameters) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    dispatch(requestPowerPoolTable());

    let effectiveBoardName = boardName;
    if (!effectiveBoardName) {
      const regionsResponse = await fetchPowerPoolRegions();
      effectiveBoardName = regionsResponse.actions.GET.board.choices[0].value;
    }

    //replace board filter by $board filter to avoid additional encoding
    const filtersWithBoard = { ...filters, $board: `board=${effectiveBoardName}` };

    const cleanFilters = Object.fromEntries(
      Object.entries(filtersWithBoard).filter(([key, value]) => value != null && value !== '' && key !== 'board')
    );
    const queryFilters = cleanFilters !== undefined ? `&${filterQuery(cleanFilters)}` : '';
    const newSort = sort;
    const queryOrder =
      newSort !== undefined ? (order ? `&order=${encodeURIComponent(newSort)}` : `&order=-${encodeURIComponent(newSort)}`) : '';
    const queryAggregate = typeAggregate && typeAggregate !== 'individual' ? `&aggregate=${encodeURIComponent(typeAggregate)}` : '';

    const queryString = `${queryFilters}${queryOrder}${queryAggregate}`;

    const link = `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/boards/power-pool`;

    return globalFetch(
      `${link}?limit=${limit}&offset=${offset}${queryString}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(powerPoolTableFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'POWER_POOL_TABLE_FAILURE')) {
          return json;
        } else if (json.count >= 0) {
          if (isModal) {
            dispatch(powerPoolTableModalSuccess());
            return dispatch(responseConsultantLeaderboardModalCompetitions(json));
          }
          return dispatch(powerPoolTableSuccess(json));
        }

        return dispatch(powerPoolTableFailure());
      })
      .catch((error: any) => {
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(powerPoolTableFailure());
      });
  };

export const getSpcLeaderboardTable =
  ({
    competitionId,
    limit,
    offset,
    filters = {},
    sort,
    order,
    typeAggregate,
    isModal = false,
    boardName,
  }: PowerPoolTableRequestParameters) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    if (!isModal) {
      dispatch(requestSpcLeaderboardTable());
    }

    let effectiveBoardName = boardName;
    if (!effectiveBoardName) {
      const choices = await fetchSpcBoardChoices();
      effectiveBoardName = choices[0]?.value;
    }

    const filtersWithBoard = { ...filters, $board: `board=${effectiveBoardName}` };

    const cleanFilters = Object.fromEntries(
      Object.entries(filtersWithBoard).filter(([key, value]) => value != null && value !== '' && key !== 'board')
    );
    const queryFilters = cleanFilters !== undefined ? `&${filterQuery(cleanFilters)}` : '';
    const newSort = sort;
    const queryOrder =
      newSort !== undefined ? (order ? `&order=${encodeURIComponent(newSort)}` : `&order=-${encodeURIComponent(newSort)}`) : '';
    const queryAggregate = typeAggregate && typeAggregate !== 'individual' ? `&aggregate=${encodeURIComponent(typeAggregate)}` : '';

    const queryString = `${queryFilters}${queryOrder}${queryAggregate}`;

    const link = `${ENDPOINTS_COMPETITIONS.consultant}/boards/spc`;

    return globalFetch(
      `${link}?limit=${limit}&offset=${offset}${queryString}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(spcLeaderboardTableFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'SPC_LEADERBOARD_TABLE_FAILURE')) {
          return json;
        } else if (json.count >= 0) {
          if (isModal) {
            dispatch(spcLeaderboardTableModalSuccess());
            return dispatch(responseConsultantLeaderboardModalCompetitions(json));
          }
          return dispatch(spcLeaderboardTableSuccess(json));
        }

        return dispatch(spcLeaderboardTableFailure());
      })
      .catch((error: any) => {
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(spcLeaderboardTableFailure());
      });
  };

interface ConsultantBoardOptions {
  type: string;
  limit: number;
  offset: number;
  filters: any;
  sort: any;
  order: boolean;
  typeAggregate: string;
  isModal?: boolean;
  isConsultant?: boolean;
}

export const getConsultantBoard =
  ({ type, limit, offset, filters, sort, order, typeAggregate, isModal = false, isConsultant = true }: ConsultantBoardOptions) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    const competitionId = state.getIn(['competitions', 'currentCompetition']);
    if (type === undefined || limit === undefined || offset === undefined) {
      return dispatch(invalidRequest());
    }
    const queryfilters = filters !== undefined ? `&${filterQuery(filters)}` : '';
    const newSort = sort;
    const queryOrder =
      newSort !== undefined ? (order ? `&order=${encodeURIComponent(newSort)}` : `&order=-${encodeURIComponent(newSort)}`) : '';
    const queryAggregate = typeAggregate && typeAggregate !== 'individual' ? `&aggregate=${encodeURIComponent(typeAggregate)}` : '';
    const link = isConsultant
      ? `${ENDPOINTS_CONSULTANT.boards}/${type}`
      : `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/boards/leader`;
    dispatch(requestConsultantLeaderboardCompetitions());
    const queryString = `${queryfilters}${queryOrder}${queryAggregate}`;

    return globalFetch(
      `${link}?limit=${limit}&offset=${offset}${queryString}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(userCompetitionsFailure()),
      {
        callbackStatus400: () => dispatch(userCompetitionsFailure()),
      }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'USER_COMPETITIONS_FAILURE')) {
          return json;
        } else if (json.count >= 0) {
          if (isModal === true) {
            return dispatch(responseConsultantLeaderboardModalCompetitions(json));
          } else {
            return dispatch(responseConsultantLeaderboardCompetitions(type, json));
          }
        } else {
          logErrorService(json, json);

          return dispatch(userCompetitionsFailure());
        }
      })
      .catch((error: any) => {
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(userCompetitionsFailure());
      });
  };

export const getFilterChoicesUniversityLeaderboard =
  (competitionId: string) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const defaultLeaderboardOptionsEndpoint = `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/boards/leader`;
    const universityLeaderboardOptionsEndpoint = `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/boards/university`;

    const requestOptions = { method: 'OPTIONS', credentials: 'include' };
    const failureCallback = () => dispatch(responseCompetitionFiltersFailure());

    try {
      dispatch(requestCompetitionFilters(competitionId, 'leader'));

      const results = await Promise.all([
        globalFetch(defaultLeaderboardOptionsEndpoint, requestOptions, dispatch, failureCallback),
        globalFetch(universityLeaderboardOptionsEndpoint, requestOptions, dispatch, failureCallback),
      ]);

      for (const json of results) {
        if (isAction(json, 'RESPONSE_COMPETITION_FILTERS_FAILURE')) {
          return json;
        }

        if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);
          return dispatch(responseCompetitionFiltersFailure());
        }

        if (t.exact(CompetitionOptions).decode(json).isLeft()) {
          ioTsValidationError(json, CompetitionOptions);
          return dispatch(responseCompetitionFiltersFailure());
        }
      }

      const [defaultLeaderboardOptions, universityLeaderboardOptions] = results.map(result => result.actions.GET);

      const defaultLeaderboardAggregationChoice = { ...defaultLeaderboardOptions.aggregate.choices[0], isDisabled: true };
      universityLeaderboardOptions.aggregate.choices.unshift(defaultLeaderboardAggregationChoice);

      return dispatch(
        responseCompetitionFiltersSuccess({
          ...universityLeaderboardOptions,
          __modalLeaderboardOptions: defaultLeaderboardOptions,
        })
      );
    } catch (error) {
      const state = getState();
      sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));
      return dispatch(responseCompetitionFiltersFailure());
    }
  };

interface FilterChoicesCompetitions {
  competitionId: string;
  competitionType?: string;
  startDateFilter?: string;
  boardRegionFilter?: string;
}

export const getFilterChoicesCompetitions =
  ({ competitionId, competitionType = 'leader', startDateFilter, boardRegionFilter }: FilterChoicesCompetitions) =>
  async (dispatch: any, getState: any): Promise<object> => {
    if (competitionId === undefined) {
      return dispatch(invalidRequest());
    }

    // The Prompt Submissions tab loads its own data and has no board filter options.
    // Skip the board-options OPTIONS request to avoid a 404 on /consultant/boards/promptSubmissions.
    if (competitionType === 'promptSubmissions') {
      return dispatch(invalidRequest());
    }

    let defaultRegion;

    if (!boardRegionFilter && competitionType === 'powerPool') {
      //get options and get the first region from options
      const regionsResponse = await fetchPowerPoolRegions();
      defaultRegion = regionsResponse.actions.GET.board.choices[0].value;
    }

    if (!boardRegionFilter && competitionType === 'spcLeaderboard') {
      const choices = await fetchSpcBoardChoices();
      defaultRegion = choices[0]?.value;
    }

    const queryRegion = boardRegionFilter || defaultRegion;

    if (competitionType === 'university') {
      return getFilterChoicesUniversityLeaderboard(competitionId)(dispatch, getState);
    }
    const state = getState();

    if (
      state.getIn(['competitions', 'fetchOptions']) &&
      state.getIn(['competitions', 'fetchOptionsId']) === competitionId &&
      state.getIn(['competitions', 'fetchOptionsType']) === competitionType
    ) {
      return dispatch(requestCompetitionFilters(competitionId, competitionType));
    }

    const dateFilter = competitionType === 'genius' && startDateFilter ? `${startDateFilter}` : '';
    const boardFilter = competitionType === 'powerPool' || competitionType === 'spcLeaderboard' ? `board=${queryRegion}` : '';
    const joinedFilters = [boardFilter, dateFilter].filter(filter => filter !== '').join('&');
    const filterString = joinedFilters !== '' ? `?${dateFilter}${boardFilter}` : '';
    const urlType = competitionType === 'spcLeaderboard' ? 'spc' : competitionType === 'powerPool' ? 'power-pool' : competitionType;
    const fetchLink =
      competitionId === 'consultant'
        ? `${ENDPOINTS_COMPETITIONS.consultant}/boards/${urlType}${filterString}`
        : `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/boards/${competitionType}`;
    dispatch(requestCompetitionFilters(competitionId, competitionType));

    return globalFetch(
      fetchLink,
      {
        method: 'OPTIONS',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseCompetitionFiltersFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_COMPETITION_FILTERS_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseCompetitionFiltersFailure());
        } else if (competitionType === 'spcLeaderboard') {
          // SPC OPTIONS already uses the standard format; bypass io-ts validation
          // since the schema allows fields (pnl, rank, etc.) not in CompetitionOptions.
          return dispatch(responseCompetitionFiltersSuccess(json.actions.GET));
        } else {
          if (t.exact(CompetitionOptions).decode(json).isRight()) {
            return dispatch(responseCompetitionFiltersSuccess(json.actions.GET));
          } else {
            // It will be reported to sentry the io-ts validation errors
            ioTsValidationError(json, CompetitionOptions);

            return dispatch(responseCompetitionFiltersFailure());
          }
        }
      })
      .catch((error: any) => {
        const state = getState();
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(responseCompetitionFiltersFailure());
      });
  };

export const getCurrentCompetition =
  (competitionId: string) =>
  async (dispatch: any, getState: any): Promise<object> => {
    dispatch(requestCompetition());

    return globalFetch(
      `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseCompetitionFailure())
    )
      .then((json: any) => {
        if (isAction(json, 'RESPONSE_COMPETITION_FAILURE')) {
          return json;
        } else if (t.exact(Results).decode(json).isRight()) {
          return dispatch(responseCompetitionSuccess(json, competitionId));
        } else {
          const state = getState();
          sendFlashMessageEmail(dispatch, json, state.getIn(['user', 'userId']));

          return dispatch(responseCompetitionFailure());
        }
      })
      .catch((error: any) => {
        const state = getState();
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(responseCompetitionFailure());
      });
  };

const filterCompetitions = (filter: string) => {
  switch (filter) {
    case 'active':
      return `&endDate!<${moment().utc().format()}`;
    case 'concluded':
      return `&endDate<${moment().utc().format()}`;
    default:
      return '';
  }
};

interface AllCompetitionsArgs {
  userId: string;
  offset?: number;
  filter?: string;
  pageSize?: number;
}

export const getAllCompetitions =
  ({ userId, offset = 10, filter = 'all', pageSize = 10 }: AllCompetitionsArgs) =>
  async (dispatch: any, getState: any): Promise<object> => {
    if (userId === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestAllCompetitions());

    return globalFetch(
      `${ENDPOINTS_COMPETITIONS.competitions}?limit=${pageSize}&offset=${offset}${filterCompetitions(filter)}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseCompetitionAcceptAgreementFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_COMPETITION_ACCEPT_AGREEMENT_FAILURE')) {
          return json;
        } else if (t.exact(CompetitionUserInfo).decode(json).isRight()) {
          if (json.count !== 0) {
            return dispatch(responseAllCompetitionsSuccess(json));
          } else {
            logErrorService(json, json);
            webSimApiErrorFlashMessages(dispatch);

            return dispatch(responseAllCompetitionsFailure());
          }
        } else {
          logErrorService(json, json);
          webSimApiErrorFlashMessages(dispatch);

          return dispatch(responseAllCompetitionsFailure());
        }
      })
      .catch((error: any) => {
        const state = getState();
        sendFlashMessageEmail(dispatch, error, state.getIn(['user', 'userId']));

        return dispatch(responseAllCompetitionsFailure());
      });
  };

export const getCompetitionsLevels =
  () =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    const levelsOptions = state.getIn(['dashboard', 'competitions', 'levelsOptions']);
    if (levelsOptions && !levelsOptions.isEmpty()) {
      return dispatch(invalidRequest());
    }

    if (state.getIn(['dashboard', 'competitions', 'isLevelsFetching'])) {
      return dispatch(invalidRequest());
    }

    dispatch(requestCompetitionsLevels());

    return globalFetch(
      `${ENDPOINTS_COMPETITIONS.competitionsLevels}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseCompetitionsLevelsFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_COMPETITIONS_LEVELS_FAILURE')) {
          return json;
        }

        if (CompetitionsLevels.decode(json).isRight()) {
          return dispatch(responseCompetitionsLevelsSuccess(json));
        } else {
          sendFlashMessageEmail(dispatch, json, state.getIn(['user', 'userId']));

          return dispatch(responseCompetitionsLevelsFailure());
        }
      })
      .catch((err: any) => {
        defaultErrorMessage(dispatch);
        logErrorService(err, err);

        return dispatch(responseCompetitionsLevelsFailure());
      });
  };
